'use client'

import { useState } from 'react'
import { ListPlus, Check, Plus, X } from 'lucide-react'
import { useVideoPlaylists, type VideoItem } from '@/lib/video-playlists'

/** Bouton « Playlist » + menu : cocher/décocher des playlists, en créer une.
 *  variant « round » : bouton rond « + » (carrousel de l'accueil vidéo), menu ouvert vers le haut. */
export default function AddToVideoPlaylist({ item, variant = 'pill' }: { item: VideoItem; variant?: 'pill' | 'round' }) {
  const { lists, create, addTo, removeFrom, has } = useVideoPlaylists()
  const [open, setOpen] = useState(false)
  const [newName, setNewName] = useState('')

  const toggle = (id: string) => {
    if (has(id, item.videoId)) removeFrom(id, item.videoId)
    else addTo(id, item)
  }
  const onCreate = () => {
    const name = newName.trim(); if (!name) return
    const id = create(name)
    addTo(id, item)
    setNewName('')
  }

  return (
    <div className="relative inline-block">
      {variant === 'round' ? (
        <button onClick={() => setOpen(o => !o)} title="Ajouter à ma liste" aria-label="Ajouter à ma liste"
          className="w-12 h-12 rounded-full grid place-items-center border-2 border-white/70 bg-black/30 backdrop-blur hover:bg-a2/35 hover:border-a4 transition">
          <Plus size={22} />
        </button>
      ) : (
        <button onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.07] border border-white/10 px-3.5 py-2 text-sm font-medium hover:bg-white/[0.12] hover:border-a2/50 transition">
          <ListPlus size={15} /> Playlist
        </button>
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute z-50 w-64 max-w-[calc(100vw-2rem)] rounded-2xl bg-spanel border border-white/[0.09] p-3 shadow-[0_18px_50px_rgba(0,0,0,.55),0_0_30px_rgb(var(--a2)/.18)] ${variant === 'round' ? 'left-0 bottom-full mb-2' : 'right-0 mt-2'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold tracking-[0.2em] text-white/55">ENREGISTRER DANS…</span>
              <button onClick={() => setOpen(false)} aria-label="Fermer" className="w-7 h-7 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/10 transition"><X size={16} /></button>
            </div>
            <div className="max-h-52 overflow-y-auto scrollbar-hide space-y-1">
              {lists.length === 0 && <div className="text-xs text-white/45 py-2">Aucune playlist. Crée-en une ci-dessous.</div>}
              {lists.map(p => {
                const inIt = has(p.id, item.videoId)
                return (
                  <button key={p.id} onClick={() => toggle(p.id)}
                    className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm text-left hover:bg-white/[0.06] transition">
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center border transition ${inIt ? 'bg-gradient-to-br from-a2 to-a1 border-transparent' : 'border-white/25'}`}>
                      {inIt && <Check size={13} />}
                    </span>
                    <span className="truncate flex-1">{p.name}</span>
                    <span className="text-xs text-white/40">{p.items.length}</span>
                  </button>
                )
              })}
            </div>
            <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/10">
              <input value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onCreate() }}
                placeholder="Nouvelle playlist" className="flex-1 min-w-0 h-9 bg-white/[0.06] border border-white/[0.09] rounded-xl px-3 text-sm placeholder-smuted focus:outline-none focus:border-a2/70 transition" />
              <button onClick={onCreate} aria-label="Créer" className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-a2 to-a1 shadow-[0_6px_18px_rgb(var(--a2)/.45)] hover:brightness-110 active:scale-95 transition flex items-center justify-center"><Plus size={18} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
